import { ChevronDown } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { Group } from '@unistocks/components/group/group';
import { toast } from '@unistocks/components/toast';
import { PortfolioDto, SyncedPortfolioDto } from '@unistocks/proxy/axios-client';
import {
  portfoliosGetQueryKey,
  usePortfoliosDeleteSyncedPortfolioMutationWithParameters
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { medium } from '@unistocks/shared/animations';
import { t } from '@unistocks/shared/localization/index.ts';
import { Option } from '@unistocks/types';
import { useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { ListItem, Separator, View } from 'tamagui';

export type PortfolioExternalAccountsProps = {
  portfolio: PortfolioDto;
};

type SyncedPortfolioAccordionItem = Option<SyncedPortfolioDto[]> &
  Pick<SyncedPortfolioDto, 'externalAccountDisplayName' | 'externalAccountImageUri'>;

export const PortfolioExternalAccounts: React.FC<PortfolioExternalAccountsProps> = ({
  portfolio
}) => {
  const queryClient = useQueryClient();

  const syncedPortfolios = useMemo<SyncedPortfolioAccordionItem[]>(() => {
    if (!portfolio?.syncedPortfolios) return [];

    const groupedItems = portfolio.syncedPortfolios.reduce((acc, syncedPortfolio) => {
      const group = acc.get(syncedPortfolio.externalAccountId);

      if (group) {
        group.value.push(syncedPortfolio);
      } else {
        acc.set(syncedPortfolio.externalAccountId, {
          key: syncedPortfolio.externalAccountId,
          value: [syncedPortfolio],
          externalAccountDisplayName: syncedPortfolio.externalAccountDisplayName,
          externalAccountImageUri: syncedPortfolio.externalAccountImageUri
        });
      }

      return acc;
    }, new Map<string, SyncedPortfolioAccordionItem>());

    return Array.from(groupedItems).map(([key, data]) => data);
  }, [portfolio]);

  const { mutate: deleteSyncedPortfolio, isPending } =
    usePortfoliosDeleteSyncedPortfolioMutationWithParameters({
      onSuccess: (_, { syncedPortfolioId }) => {
        const syncedPortfolioName = portfolio.syncedPortfolios.find(
          item => item.id === syncedPortfolioId
        )?.name;

        queryClient.invalidateQueries({ queryKey: portfoliosGetQueryKey(portfolio.id) });
        toast.success(t('::SuccessfullyDeletedSyncedPortfolio', { 0: syncedPortfolioName }));
      }
    });

  return (
    <View overflow={'hidden'} borderRadius={'$2'}>
      <Accordion
        collapsible
        type={'single'}
        defaultValue={syncedPortfolios.length ? syncedPortfolios[0].key : undefined}
      >
        <Group separator={<Separator />}>
          {syncedPortfolios.map(item => {
            const { key, externalAccountDisplayName, externalAccountImageUri, value } = item;

            return (
              <Group.Item key={key}>
                <Accordion.Item key={key} value={key}>
                  <Accordion.Trigger>
                    {({ open }) => (
                      <ListItem cursor={'pointer'}>
                        <View pr={'$4'}>
                          <Image rounded size={'$5'} source={externalAccountImageUri} />
                        </View>

                        <ListItem.Text size={'$5'} fontWeight={'$7'} color={'$headingFont'}>
                          {externalAccountDisplayName}
                        </ListItem.Text>

                        <Animated.View
                          style={{
                            transform: [{ rotate: open ? '180deg' : '0deg' }],
                            transitionProperty: 'transform',
                            transitionDuration: medium.duration!,
                            transitionTimingFunction: 'ease-out'
                          }}
                        >
                          <ChevronDown size={'$2'} />
                        </Animated.View>
                      </ListItem>
                    )}
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <Group separator={<Separator />}>
                      {value.map(syncedPortfolio => {
                        const accountType = t(
                          `::Enum:ExternalAccountType:${syncedPortfolio.portfolioType}`
                        );

                        return (
                          <Group.Item key={syncedPortfolio.id}>
                            <ListItem
                              gap={'$3'}
                              title={syncedPortfolio.name}
                              subTitle={accountType}
                              icon={
                                <Image
                                  rounded
                                  size={'$2.5'}
                                  source={syncedPortfolio.externalAccountImageUri}
                                />
                              }
                              scaleIcon={1.5}
                              iconAfter={() => (
                                <ConfirmDelete
                                  title={syncedPortfolio.name}
                                  message={t('::Portfolio:SyncedPortfolio:Delete')}
                                  isLoading={isPending}
                                  onConfirm={() =>
                                    deleteSyncedPortfolio({
                                      id: portfolio.id,
                                      syncedPortfolioId: syncedPortfolio.id
                                    })
                                  }
                                />
                              )}
                              pressStyle={{ backgroundColor: 'transparent' }}
                            />
                          </Group.Item>
                        );
                      })}
                    </Group>
                  </Accordion.Content>
                </Accordion.Item>
              </Group.Item>
            );
          })}
        </Group>
      </Accordion>
    </View>
  );
};
